import React, { Component, ReactNode } from 'react';

/**
 * StrategyErrorBoundary Component
 *
 * React error boundary to catch errors in the strategy pages.
 * Displays a friendly error message with a retry button.
 *
 * Features:
 * - Catches React component errors
 * - Displays user-friendly error message
 * - Provides retry button to reset error state
 * - Logs errors to console for debugging
 *
 * Usage:
 * ```tsx
 * <StrategyErrorBoundary>
 *   <StrategyPage />
 * </StrategyErrorBoundary>
 * ```
 */

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class StrategyErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * Update state when an error is caught
   */
  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Log error details for debugging
   */
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('[StrategyErrorBoundary] Caught error:', error);
    console.error('[StrategyErrorBoundary] Error info:', errorInfo);

    this.setState({
      errorInfo,
    });
  }

  /**
   * Reset error state and retry
   */
  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  /**
   * Reload the entire page (last resort)
   */
  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8 dark:bg-gray-900">
          <div className="w-full max-w-2xl">
            {/* Error Card */}
            <div className="rounded-lg border border-red-200 bg-white p-8 shadow-lg dark:border-red-800 dark:bg-gray-800">
              {/* Error Icon */}
              <div className="mb-4 flex justify-center">
                <div className="rounded-full bg-red-100 p-3 dark:bg-red-900/20">
                  <svg
                    className="h-12 w-12 text-red-600 dark:text-red-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
              </div>

              {/* Error Title */}
              <h1 className="mb-2 text-center text-2xl font-bold text-gray-900 dark:text-gray-100">
                Something Went Wrong
              </h1>

              {/* Error Description */}
              <p className="mb-6 text-center text-gray-600 dark:text-gray-400">
                An unexpected error occurred while loading the strategy page.
                Please try again.
              </p>

              {/* Error Details (Collapsible) */}
              {this.state.error && (
                <details className="mb-6 rounded-md border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300">
                    Technical Details
                  </summary>
                  <div className="mt-3 space-y-2">
                    <div>
                      <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                        Error:
                      </span>
                      <pre className="mt-1 overflow-auto rounded bg-white p-2 text-xs text-red-600 dark:bg-gray-800 dark:text-red-400">
                        {this.state.error.message}
                      </pre>
                    </div>
                    {this.state.error.stack && (
                      <div>
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                          Stack Trace:
                        </span>
                        <pre className="mt-1 max-h-40 overflow-auto rounded bg-white p-2 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                          {this.state.error.stack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
                <button
                  onClick={this.handleRetry}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-6 py-3 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:bg-blue-500 dark:hover:bg-blue-600"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Try Again
                </button>
                <button
                  onClick={this.handleReload}
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-6 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Reload Page
                </button>
              </div>

              {/* Back to Home Link */}
              <div className="mt-6 text-center">
                <a
                  href="/"
                  className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  Back to Home
                </a>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default StrategyErrorBoundary;
